import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { Course } from "./types";
import { supportEmail } from "./support";
import { localizedPath, seoLanguageTags, seoLanguages, splitLocalizedPath, type SeoLanguage } from "./seo-content";

export type SeoSchema = Record<string, unknown> | Array<Record<string, unknown>>;

type SeoProps = {
  title: string;
  description: string;
  noIndex?: boolean;
  schema?: (origin: string) => SeoSchema;
  image?: string;
  imageAlt?: string;
  localized?: boolean;
};

function setMeta(selector: string, attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) { element = document.createElement("meta"); element.setAttribute(attribute, key); document.head.append(element); }
  element.content = content;
}

export function Seo({ title, description, noIndex = false, schema, image, imageAlt, localized = false }: SeoProps) {
  const { pathname } = useLocation();
  useEffect(() => {
    const origin = window.location.origin;
    const canonicalUrl = `${origin}${pathname === "/" ? "/" : pathname.replace(/\/$/, "")}`;
    const resolvedImage = image ? (image.startsWith("http") ? image : `${origin}${image}`) : `${origin}/og-study-space-v2.webp`;
    const resolvedImageAlt = imageAlt || title;
    document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    setMeta('meta[property="og:image"]', "property", "og:image", resolvedImage);
    setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", resolvedImageAlt);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", resolvedImage);
    setMeta('meta[name="twitter:image:alt"]', "name", "twitter:image:alt", resolvedImageAlt);
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.append(canonical); }
    canonical.href = canonicalUrl;
    document.head.querySelectorAll('link[data-study-space-hreflang]').forEach(element => element.remove());
    if (localized) {
      const { basePath } = splitLocalizedPath(pathname);
      for (const language of seoLanguages) {
        const alternate = document.createElement("link");
        alternate.rel = "alternate";
        alternate.hreflang = seoLanguageTags[language];
        alternate.href = `${origin}${localizedPath(basePath, language)}`;
        alternate.dataset.studySpaceHreflang = "true";
        document.head.append(alternate);
      }
      const fallback = document.createElement("link");
      fallback.rel = "alternate";
      fallback.hreflang = "x-default";
      fallback.href = `${origin}${localizedPath(basePath, "en")}`;
      fallback.dataset.studySpaceHreflang = "true";
      document.head.append(fallback);
    }
    let robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (noIndex) {
      if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.append(robots); }
      robots.content = "noindex, nofollow";
    } else robots?.remove();
    const existingSchema = document.getElementById("study-space-seo-schema");
    if (schema) {
      const script = existingSchema || document.createElement("script");
      script.id = "study-space-seo-schema";
      script.setAttribute("type", "application/ld+json");
      script.textContent = JSON.stringify(schema(origin));
      if (!existingSchema) document.head.append(script);
    } else existingSchema?.remove();
  }, [description, image, imageAlt, localized, noIndex, pathname, schema, title]);
  return null;
}

export function organizationSchema(origin: string): SeoSchema {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${origin}/#organization`,
    name: "Study Space",
    legalName: "MLW, LLC",
    url: origin,
    logo: { "@type": "ImageObject", "@id": `${origin}/#logo`, url: `${origin}/brand/study-space-logo.webp` },
    email: supportEmail,
    contactPoint: { "@type": "ContactPoint", contactType: "customer support", email: supportEmail, availableLanguage: ["English", "Chinese", "Thai", "Burmese", "Vietnamese", "Spanish"] },
    knowsAbout: ["Professional licensing examinations", "Massage therapy continuing education", "Cosmetology continuing education", "Professional licensing updates", "Dog grooming education"],
  };
}

export function websiteSchema(origin: string): SeoSchema {
  return [
    organizationSchema(origin) as Record<string, unknown>,
    { "@context": "https://schema.org", "@type": "WebSite", "@id": `${origin}/#website`, name: "Study Space", url: `${origin}/`, inLanguage: Object.values(seoLanguageTags), publisher: { "@id": `${origin}/#organization` } },
  ];
}

export function homepageSchema(language: SeoLanguage) {
  return (origin: string): SeoSchema => [
    ...(websiteSchema(origin) as Array<Record<string, unknown>>),
    { "@context": "https://schema.org", "@type": "WebPage", "@id": `${origin}${localizedPath("/", language)}#webpage`, name: "Study Space", url: `${origin}${localizedPath("/", language)}`, inLanguage: seoLanguageTags[language], isPartOf: { "@id": `${origin}/#website` }, about: { "@id": `${origin}/#organization` } },
  ];
}

export function breadcrumbSchema(origin: string, items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({ "@type": "ListItem", position: index + 1, name: item.name, item: `${origin}${item.path}` })),
  };
}

export function webPageSchema(input: { title: string; description: string; path: string; breadcrumbs: Array<{ name: string; path: string }> }) {
  return (origin: string): SeoSchema => [
    organizationSchema(origin) as Record<string, unknown>,
    { "@context": "https://schema.org", "@type": "WebPage", "@id": `${origin}${input.path}#webpage`, name: input.title, description: input.description, url: `${origin}${input.path}`, isPartOf: { "@id": `${origin}/#website` }, about: { "@id": `${origin}/#organization` } },
    breadcrumbSchema(origin, input.breadcrumbs),
  ];
}

function defaultCoursePriceCents(course: Course) {
  if (course.id === "professional-dog-grooming") return 678500;
  if (course.id === "massage-ce-12" || course.id === "cosmetology-ce-4") return 4900;
  if (course.id === "texas-prep" || course.id === "texas-cosmetology-prep" || course.id === "texas-esthetician-prep" || course.id === "texas-barber-prep") return 19900;
  return 24900;
}

export function courseSchema(course: Course) {
  return (origin: string): SeoSchema => [
    organizationSchema(origin) as Record<string, unknown>,
    {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": `${origin}/courses/${course.id}#course`,
      name: course.title,
      description: course.description,
      url: `${origin}/courses/${course.id}`,
      provider: { "@id": `${origin}/#organization` },
      courseMode: course.id === "professional-dog-grooming" ? "onsite" : "online",
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: ((course.tuitionCents?.[0] ?? defaultCoursePriceCents(course)) / 100).toFixed(2),
        availability: course.checkoutAvailable === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
        url: `${origin}/courses/${course.id}`,
      },
    },
    breadcrumbSchema(origin, [{ name: "Home", path: "/" }, { name: "Courses", path: "/#programs" }, { name: course.title, path: `/courses/${course.id}` }]),
  ];
}
